import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Zap } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminSqueezeRadarButton — Round 102f (Feb 2026).
 *
 * Admin-only, dashboard-only button that manually fires the
 * Squeeze Radar warmer (`services/squeeze_radar_warmer.py`) via
 * POST /api/admin/squeeze-radar/fire. Endpoint spawns a background
 * task and returns immediately; we poll `/admin/squeeze-radar/status`
 * every 6 seconds until `in_progress` flips false, then show the
 * new candidate count + last-refresh age on the pill.
 *
 * Non-admin users see nothing (early return).
 */
export default function AdminSqueezeRadarButton() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [universeSize, setUniverseSize] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/squeeze-radar/status`, { withCredentials: true });
      if (typeof data?.snapshot?.candidates_with_data === 'number') {
        setCandidates(data.snapshot.candidates_with_data);
      }
      // Round P107b (Feb 2026) — Surface `universe_size` too so
      // preview vs production can be diagnosed at a glance. A
      // healthy prod should show ~400+ tickers scanned; a broken
      // one collapses to just the user portfolio + watchlist union
      // (single-digit to low-double-digit universe).
      if (typeof data?.snapshot?.universe_size === 'number') {
        setUniverseSize(data.snapshot.universe_size);
      }
      if (data?.snapshot?.generated_at) {
        setLastRefresh(data.snapshot.generated_at);
      }
      return !!data?.state?.in_progress;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'admin') return undefined;
    let cancelled = false;
    fetchStatus().then((running) => {
      if (cancelled) return;
      if (running) setBusy(true);
    });
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [user]);

  if (!user || user.role !== 'admin') return null;

  const fmtAge = (iso) => {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    const h = Math.floor(ms / 3600000);
    if (h < 1) {
      const m = Math.max(1, Math.floor(ms / 60000));
      return `${m}m ago`;
    }
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/squeeze-radar/fire`, {}, { withCredentials: true }
      );
      if (data?.status === 'already_running') {
        toast.info('Squeeze Radar warmer already running', {
          description: 'Another admin fired it recently. Polling for completion…',
        });
      } else {
        toast.success('Squeeze Radar warmer started', {
          description: 'Runs in background — should finish in 1-2 minutes. Pill updates when done.',
          duration: 6000,
        });
      }
      // Poll every 6s until the warmer reports done (max 10 min).
      let ticks = 0;
      pollRef.current = setInterval(async () => {
        ticks += 1;
        const stillRunning = await fetchStatus();
        if (!stillRunning || ticks >= 100) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setBusy(false);
          if (!stillRunning && ticks > 0) {
            toast.success('Squeeze Radar snapshot refreshed', {
              description: 'Dashboard strip will show the new candidates on next page load.',
              duration: 6000,
            });
          }
        }
      }, 6000);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Warmer fire failed';
      toast.error('Could not start Squeeze Radar warmer', { description: msg });
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-muted-foreground hover:text-amber-600 dark:hover:text-amber-400 transition-colors px-2 py-1 rounded-md hover:bg-amber-50 dark:hover:bg-amber-950/30 border border-dashed border-muted-foreground/30 disabled:opacity-50"
      title="Admin only: manually fire the Squeeze Radar warmer. Badge reads UNIVERSE→CANDIDATES (e.g. 425→25 means 425 tickers scanned, 25 had valid short-interest + float data). Universe = owned + watchlist tickers ∪ top-400 U.S. common stocks by avg_daily_volume. If your universe shows single digits, `avg_daily_volume` isn't populated in stock_cache. Scans Massive short-interest snapshot + days-to-cover, ranks by squeeze-risk score, stores top 50 to db.squeeze_radar_snapshot. ~1-2 minutes for the full walk. Safe to re-run — idempotent."
      data-testid="admin-squeeze-radar-btn"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
      Admin: Squeeze Radar
      {candidates !== null && (
        <span className="ml-1 text-amber-600 dark:text-amber-400 font-mono" data-testid="admin-squeeze-radar-count">
          {universeSize !== null ? `${universeSize}→` : ''}{candidates}
          {lastRefresh ? <span className="text-muted-foreground/60"> · {fmtAge(lastRefresh)}</span> : null}
        </span>
      )}
    </button>
  );
}
